import { apiClient } from '../client';

/**
 * Client for `/api/comms/comments/*` — comment threads on EOS rocks,
 * to-dos, and issues (Stream E, ADR-027 §3.3). Kept 1:1 with
 * `server/src/routes/comms/comments.routes.ts`.
 *
 * Threads are flat and org-transparent (like their host EOS surfaces):
 * any member reads and posts, authors edit their own, deletes are soft.
 * The read returns ASC order with deleted rows collapsed to tombstones
 * — `{id, deletedAt, createdAt}` only, body/author stripped server-side.
 */

export type CommentEntityType = 'rock' | 'todo' | 'issue';

/** A live comment row. `deletedAt` is always null (tombstones are separate). */
export interface CommentView {
  id: string;
  entityType: CommentEntityType;
  entityId: string;
  bodyText: string;
  authorMembershipId: string | null;
  authorNameSnapshot: string;
  editedAt: string | null;
  deletedAt: null;
  createdAt: string;
  updatedAt: string;
}

/** A soft-deleted comment, reduced to placement data (renders as "Comment removed"). */
export interface CommentTombstone {
  id: string;
  deletedAt: string;
  createdAt: string;
}

export type ThreadItem = CommentView | CommentTombstone;

/** Narrow a thread row to its tombstone form (deleted rows carry no body). */
export function isTombstone(item: ThreadItem): item is CommentTombstone {
  return item.deletedAt !== null;
}

export function commentsQueryKey(
  entityType: CommentEntityType,
  entityId: string,
): [string, string, CommentEntityType, string] {
  return ['comms', 'comments', entityType, entityId];
}

export interface ListThreadQuery {
  entityType: CommentEntityType;
  entityId: string;
}

/** Full thread, ASC by creation, tombstones included. */
export async function listThread(q: ListThreadQuery): Promise<ThreadItem[]> {
  const params = new URLSearchParams({ entityType: q.entityType, entityId: q.entityId });
  const res = await apiClient.get<{ data: ThreadItem[] }>(`/api/comms/comments?${params.toString()}`);
  return res.data.data;
}

export interface CreateCommentRequest {
  entityType: CommentEntityType;
  entityId: string;
  /** 1..4000 chars, trimmed server-side. */
  bodyText: string;
}

export async function createComment(body: CreateCommentRequest): Promise<CommentView> {
  const res = await apiClient.post<{ data: CommentView }>('/api/comms/comments', body);
  return res.data.data;
}

/** Author only — sets `editedAt` server-side. */
export async function editComment(id: string, body: { bodyText: string }): Promise<CommentView> {
  const res = await apiClient.put<{ data: CommentView }>(
    `/api/comms/comments/${encodeURIComponent(id)}`,
    body,
  );
  return res.data.data;
}

/** Soft delete — author, or a `manage_eos`/superadmin moderator. Returns 204. */
export async function deleteComment(id: string): Promise<void> {
  await apiClient.delete(`/api/comms/comments/${encodeURIComponent(id)}`);
}
